/**
 * @file tasks-query.ts
 * @description Состояние страницы «Задачи» в адресе (phase19.31, SC60;
 * crm.md T7): отбор «Мои / Все / Просрочено / Без исполнителя», виды дел и
 * «Показать выполненные».
 *
 * Значения по умолчанию в адрес не пишутся: «Мои», все три вида, только
 * открытые. F5 и «назад» из карточки клиента возвращают тот же отбор.
 * Отборы, пилюли видов и пустые состояния — реестрами, а не ветками в
 * разметке: новый отбор = запись.
 */

import type { ActivityKind, TaskCountsDto, listActivities } from "../../../lib/api-crm";

/** Отбор страницы — пилюли со счётчиками. */
export type TaskScope = "mine" | "all" | "overdue" | "unassigned";

/** Виды-дела: только у них есть срок, исполнитель и выполнение (SC60). */
export type TaskKind = Extract<ActivityKind, "TASK" | "CALL" | "MEETING">;

export const TASK_KINDS: readonly TaskKind[] = ["TASK", "CALL", "MEETING"];

export interface TasksQuery {
  scope: TaskScope;
  /** Выбранные виды; пусто — все три. */
  kinds: TaskKind[];
  /** Показывать и выполненные. */
  showDone: boolean;
}

/** Сколько дел берём одним запросом (предел сервера — 200). */
export const TASKS_LIMIT = 200;

/** Пилюли отбора: подпись и какое число из `counts` показать. */
export const TASK_SCOPE_CHIPS: readonly { value: TaskScope; label: string; countKey: keyof TaskCountsDto }[] = [
  { value: "mine", label: "Мои", countKey: "mine" },
  { value: "all", label: "Все", countKey: "all" },
  { value: "overdue", label: "Просрочено", countKey: "overdue" },
  { value: "unassigned", label: "Без исполнителя", countKey: "unassigned" },
];

/** Подписи пилюль видов — во множественном числе. */
export const TASK_KIND_FILTER_LABEL: Record<TaskKind, string> = {
  TASK: "Задачи",
  CALL: "Звонки",
  MEETING: "Встречи",
};

/** Имена параметров адреса. */
const KEY = { scope: "scope", kinds: "kind", done: "done" } as const;

const SCOPES = TASK_SCOPE_CHIPS.map((c) => c.value);

function isScope(raw: string | null): raw is TaskScope {
  return raw !== null && (SCOPES as readonly string[]).includes(raw);
}

/** Адрес → состояние; мусор тихо заменяется умолчаниями. */
export function parseTasksQuery(params: URLSearchParams): TasksQuery {
  const raw = params.get(KEY.scope);
  const picked = new Set((params.get(KEY.kinds) ?? "").split(","));
  return {
    scope: isScope(raw) ? raw : "mine",
    // Порядок — по реестру, не по адресу: одно состояние — один адрес.
    kinds: TASK_KINDS.filter((k) => picked.has(k)),
    showDone: params.get(KEY.done) === "1",
  };
}

/** Состояние → адрес; умолчания не пишем. */
export function writeTasksQuery(query: TasksQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.scope !== "mine") params.set(KEY.scope, query.scope);
  if (query.kinds.length > 0 && query.kinds.length < TASK_KINDS.length) params.set(KEY.kinds, query.kinds.join(","));
  if (query.showDone) params.set(KEY.done, "1");
  return params;
}

/** Параметры запроса к серверу: отбор, виды, статус и числа вкладок. */
export function toListTasksParams(query: TasksQuery): NonNullable<Parameters<typeof listActivities>[0]> {
  return {
    kinds: query.kinds.length > 0 ? query.kinds : [...TASK_KINDS],
    status: query.showDone ? "all" : "open",
    mine: query.scope === "mine" || undefined,
    overdue: query.scope === "overdue" || undefined,
    unassigned: query.scope === "unassigned" || undefined,
    counts: true,
    limit: TASKS_LIMIT,
  };
}

/** Включённые фильтры кроме отбора: виды и «выполненные» (число на кнопке шторки). */
export function tasksFilterCount(query: TasksQuery): number {
  const kindsFiltered = query.kinds.length > 0 && query.kinds.length < TASK_KINDS.length;
  return [kindsFiltered, query.showDone].filter(Boolean).length;
}

/** «Сбросить фильтры»: все виды, только открытые; отбор остаётся. */
export function resetTasksFilters(query: TasksQuery): TasksQuery {
  return { ...query, kinds: [], showDone: false };
}

/** Включить/выключить вид в фильтре. */
export function toggleTaskKind(kinds: readonly TaskKind[], kind: TaskKind): TaskKind[] {
  const next = new Set(kinds);
  if (next.has(kind)) next.delete(kind);
  else next.add(kind);
  return TASK_KINDS.filter((k) => next.has(k));
}

/** Текст пустого списка и признак «пусто под фильтром» (со сбросом). */
export interface TasksEmptyText {
  title: string;
  description?: string;
  filtered: boolean;
}

/** Пустые состояния отборов без фильтров — реестром. */
const SCOPE_EMPTY: Record<TaskScope, Omit<TasksEmptyText, "filtered">> = {
  mine: { title: "Задач нет", description: "Здесь появятся задачи, звонки и встречи, назначенные вам" },
  all: { title: "Задач нет", description: "Добавьте задачу кнопкой «+ Задача» — к клиенту, сделке или заказу" },
  overdue: { title: "Просроченных задач нет — отлично" },
  unassigned: { title: "Все задачи распределены" },
};

export function tasksEmptyText(query: TasksQuery): TasksEmptyText {
  if (tasksFilterCount(query) > 0) {
    return { title: "Нет задач по выбранным условиям", description: "Измените виды или сбросьте фильтры", filtered: true };
  }
  return { ...SCOPE_EMPTY[query.scope], filtered: false };
}
